// ─────────────────────────────────────────────
// Mi espacio — files read in the browser.
// A file becomes text here and only the text is sent (in parts) to the server;
// the file itself is never uploaded or stored. Reuses the reading of .txt and
// .docx of the transcripts (diagnosticTranscriptFile.js). PDFs are read page by
// page with pdf.js: each page keeps its number, and pages without text
// (scanned) are counted, because they need OCR, which is not available yet.
// Audio and video are not transcribed.
// ─────────────────────────────────────────────
import { extractTranscriptText } from './diagnosticTranscriptFile.js';
import { lifeFileKind, splitText, splitTranscript, LIFE_FILE_MAX_BYTES, LIFE_CONTENT_CHUNK } from './ownerSpaceConfig.js';

export class LifeFileError extends Error {
  constructor(code) { super(code); this.code = code; }
}

/** A page with fewer characters than this has no usable text (a scan, an image). */
const MIN_PAGE_CHARS = 15;

async function pdfPages(arrayBuffer) {
  let pdfjs;
  try {
    pdfjs = await import('pdfjs-dist');
    const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
    pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  } catch { throw new LifeFileError('unreadable'); }
  let doc;
  try { doc = await pdfjs.getDocument({ data: new Uint8Array(arrayBuffer), isEvalSupported: false }).promise; }
  catch { throw new LifeFileError('unreadable'); }
  const pages = [];
  for (let n = 1; n <= doc.numPages; n++) {
    const page = await doc.getPage(n);
    const content = await page.getTextContent();
    // Lines: pdf.js marks the end of a line with hasEOL
    const text = content.items.map(i => (i.str || '') + (i.hasEOL ? '\n' : ' ')).join('').replace(/[ \t]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
    pages.push({ n, text });
  }
  await doc.destroy().catch(() => {});
  return pages;
}

/**
 * The pieces of a file and what was found:
 * { pieces: [{ text, page?, timeSeconds? }], info: { format, fileName, pages, pagesWithText, pagesWithoutText, chars } }
 * Throws LifeFileError('media' | 'unsupported' | 'too_large' | 'unreadable' | 'empty' | 'needs_ocr'),
 * with the info attached for 'needs_ocr' (so it can be recorded as such).
 */
export async function readLifeFile(file) {
  const kind = lifeFileKind(file.name);
  if (kind === 'media' || kind === 'unsupported') throw new LifeFileError(kind);
  if (file.size > LIFE_FILE_MAX_BYTES) throw new LifeFileError('too_large');
  const buffer = await file.arrayBuffer();
  const fileName = file.name.slice(0, 160);
  if (kind === 'pdf') {
    const pages = await pdfPages(buffer);
    const withText = pages.filter(p => p.text.length >= MIN_PAGE_CHARS);
    const info = { format: 'pdf', fileName, pages: pages.length, pagesWithText: withText.length, pagesWithoutText: pages.filter(p => p.text.length < MIN_PAGE_CHARS).map(p => p.n), chars: withText.reduce((n, p) => n + p.text.length, 0) };
    if (!withText.length) { const e = new LifeFileError('needs_ocr'); e.info = info; throw e; }
    const pieces = withText.flatMap(p => splitText(p.text, LIFE_CONTENT_CHUNK).map(text => ({ text, page: p.n })));
    return { pieces, info };
  }
  let text;
  try { text = kind === 'docx' ? await extractTranscriptText('archivo.docx', buffer) : new TextDecoder('utf-8').decode(buffer); }
  catch { throw new LifeFileError('unreadable'); }
  text = String(text || '').trim();
  if (!text) throw new LifeFileError('empty');
  const pieces = kind === 'subtitles' ? splitTranscript(text) : splitText(text).map(t => ({ text: t }));
  return { pieces, info: { format: kind, fileName, chars: text.length } };
}

/** Pasted text: plain, or a transcript with minutes. */
export function piecesFromPaste(text, transcript) {
  const t = String(text || '').trim();
  if (!t) return [];
  return transcript ? splitTranscript(t) : splitText(t).map(x => ({ text: x }));
}

/** Pieces grouped in requests of at most maxChars. */
export function batches(pieces, maxChars) {
  const out = [];
  let cur = [], n = 0;
  for (const p of pieces) {
    if (cur.length && n + p.text.length > maxChars) { out.push(cur); cur = []; n = 0; }
    cur.push(p); n += p.text.length;
  }
  if (cur.length) out.push(cur);
  return out;
}
